import type { MandateDetail, MandateView, OccurrenceView } from "../api/types.js";
import { formatMoney, fullWhen, lastDigits, proxyTail, sgDay, signedMoney } from "../lib/format.js";
import { clock } from "../lib/format.js";
import type { IconId } from "../lib/icon.js";
import { atMostRow, dateWords, eachTimeRow, endsRow, fullDate, mandateFrom, mandateName, paymentAmount, ruleIcon, ruleLine, ruleShort, rowRule, shortDate, type SchedKV } from "./schedule-words.js";

/**
 * DCTA-209 F57–F59 (09-scheduled.md rows 28a–28l, §0.2 Data → screen) — the Scheduled list, Activity's section and a
 * schedule's screen, from route 1 (`MandateView`) and route 2 (`MandateDetail`) only. Every date is the bank's, formatted;
 * every payment's `line` is the bank's, verbatim; the counts are the bank's strings. Nothing here computes a date or a
 * figure. A schedule is named as its card named the step (the customer's word for a payee, else the registry's name).
 */

/** A schedule as a row (28b, 28c): who, the rule with its date kept whole, the amount; a cap says "at most". */
export interface ScheduleRow {
  readonly id: string;
  readonly ic: IconId;
  readonly who: string;
  /** The words before the date: "Every month · next ", "Once · ", "Stopped ", "Once · sent ". */
  readonly rule: string;
  /** "Sun 1 Nov" — never broken across lines. */
  readonly date: string;
  readonly amount: string;
  readonly max: boolean;
  readonly muted: boolean;
  /** What a screen reader hears for the row, in order: who, rule, next date, amount, "at most". */
  readonly label: string;
}

const amountOf = (m: MandateView): { readonly amount: string; readonly max: boolean } =>
  m.amount.kind === "fixed" ? { amount: formatMoney(m.amount.value, m.amount.currency), max: false } : { amount: formatMoney(m.amount.cap.value, m.amount.cap.currency), max: true };

/** A finished single payment's word, by its last payment and who it went to (Data → screen: never parsed out of `line`). */
function onceWord(m: MandateView): string | undefined {
  const last = m.last;
  if (last === undefined) return undefined;
  if (last.state === "skipped") return "skipped";
  if (last.state !== "executed") return undefined;
  return m.to.kind === "biller" ? "paid" : m.to.kind === "own_account" ? "done" : "sent";
}

type EndReason = NonNullable<MandateView["ended"]>["reason"];
const ENDED_WORD: Readonly<Record<EndReason, string>> = { cancelled: "Stopped", superseded: "Replaced", completed: "Finished", expired: "Ended" };

/** Still running: the bank's status says so and it has not ended. */
export const isActive = (m: MandateView): boolean => m.status === "active" && m.ended === undefined;

/** The active schedules, soonest first (the next date, else the first), whatever order they came in. */
export function activeSoonest(list: readonly MandateView[]): MandateView[] {
  const next = (m: MandateView): string => m.next_due_on ?? m.schedule.first_on;
  return list.filter(isActive).sort((a, b) => next(a).localeCompare(next(b)) || a.mandate_id.localeCompare(b.mandate_id));
}

function row(m: MandateView): ScheduleRow {
  const { amount, max } = amountOf(m);
  const who = mandateName(m);
  const at = max ? `${amount} at most` : amount;
  if (isActive(m)) {
    const next = m.next_due_on ?? m.schedule.first_on;
    const once = m.schedule.cadence === "once";
    const long = dateWords(next)?.long ?? next;
    return {
      id: m.mandate_id,
      ic: ruleIcon(m.schedule),
      who,
      rule: once ? "Once · " : `${rowRule(m.schedule)} · next `,
      date: shortDate(next),
      amount,
      max,
      muted: false,
      label: once ? `${who}, once, ${long}, ${at}` : `${who}, ${ruleShort(m.schedule)}, next ${long}, ${at}`,
    };
  }
  // Ended: by its `ended`, or — not there yet — by its status alone, undated.
  const reason: EndReason = m.ended?.reason ?? (m.status === "active" ? "expired" : m.status);
  const day = m.ended === undefined ? undefined : sgDay(m.ended.at);
  const long = day === undefined ? "" : ` ${dateWords(day)?.long ?? day}`;
  const word = m.schedule.cadence === "once" && reason === "completed" ? onceWord(m) : undefined;
  const rule = word === undefined ? `${ENDED_WORD[reason]} ` : `Once · ${word} `;
  return {
    id: m.mandate_id,
    ic: ruleIcon(m.schedule),
    who,
    rule,
    date: day === undefined ? "" : shortDate(day),
    amount,
    max,
    muted: true,
    label: word === undefined ? `${who}, ${ENDED_WORD[reason].toLowerCase()}${long}, ${at}` : `${who}, once, ${word}${long}, ${at}`,
  };
}

/** The list (28c): every active schedule soonest first, then the ended ones newest first — whatever order they came in. */
export function scheduleRows(list: readonly MandateView[]): { readonly active: ScheduleRow[]; readonly ended: ScheduleRow[] } {
  const ended = list.filter((m) => !isActive(m)).sort((a, b) => (b.ended?.at ?? "").localeCompare(a.ended?.at ?? ""));
  return { active: activeSoonest(list).map(row), ended: ended.map(row) };
}

export interface ScheduledSection {
  readonly rows: readonly ScheduleRow[];
  readonly seeAll?: { readonly label: string; readonly aria: string };
}

/**
 * 28a: Activity's section — at most three active rows, soonest first; "See all {n}" when there are more than three, or
 * whenever any has ended (the Ended group lives on the list). An account filter keeps the schedules that take money from
 * that account. Nothing active (after the filter): no section.
 */
export function scheduledSection(list: readonly MandateView[], account?: string): ScheduledSection | undefined {
  const mine = account === undefined || account === "all" ? list : list.filter((m) => m.from.account_id === account);
  const { active, ended } = scheduleRows(mine);
  if (active.length === 0) return undefined;
  const n = String(active.length);
  return { rows: active.slice(0, 3), ...(active.length > 3 || ended.length > 0 ? { seeAll: { label: `See all ${n}`, aria: `See all ${n} scheduled payments` } } : {}) };
}

/** One row of a schedule's history (28h): the word carries the state, the tile's mark only repeats it. */
export interface HistRow {
  readonly ic: IconId;
  readonly tone: "ok" | "next" | "";
  readonly title: string;
  /** The state's word in bold: the bank's line up to its " — ", or the whole line. */
  readonly st: string;
  readonly rest: string;
  readonly amount: string;
  readonly muted: boolean;
}

export interface ScheduleDetailModel {
  readonly hero: {
    readonly who: string;
    readonly sub: string;
    readonly amount: string;
    readonly max: boolean;
    readonly rule: string;
    readonly status: "Next payment" | "Status";
    readonly flag: string;
  };
  readonly kv: readonly SchedKV[];
  readonly history: readonly HistRow[];
  /** Stop and P1's change — only while the schedule is active. */
  readonly actions?: { readonly stop: string; readonly change: string };
}

/** The bank's line split for its bold word — its text is unchanged ("Skipped" + " — not enough money."). */
export function splitLine(line: string): { readonly st: string; readonly rest: string } {
  const i = line.indexOf(" — ");
  return i < 0 ? { st: line, rest: "" } : { st: line.slice(0, i), rest: line.slice(i) };
}

function occurrenceRow(m: MandateView, o: OccurrenceView): HistRow {
  const { st, rest } = splitLine(o.line);
  const a = paymentAmount(m, o);
  if (o.state === "executed") return { ic: "i-check", tone: "ok", title: fullDate(o.due_on), st, rest, amount: a === undefined ? "" : signedMoney(a.value, a.currency, "debit"), muted: false };
  return { ic: "i-minus", tone: "", title: fullDate(o.due_on), st, rest, amount: a === undefined ? "" : formatMoney(a.value, a.currency), muted: true };
}

/** 28f: the flag of a schedule that has ended (a finished once: its payment's word). With no `ended` yet, its status. */
function endedFlag(m: MandateView): string {
  const reason = m.ended?.reason ?? (m.status === "active" ? undefined : m.status);
  if (reason === undefined) return "";
  if (m.schedule.cadence === "once" && reason === "completed") {
    const w = onceWord(m);
    if (w !== undefined) return `${w.charAt(0).toUpperCase()}${w.slice(1)}`;
  }
  return ENDED_WORD[reason];
}

/** A schedule's screen (28f–28i), from route 2. */
export function scheduleDetail(m: MandateDetail): ScheduleDetailModel {
  const { amount, max } = amountOf(m);
  const once = m.schedule.cadence === "once";
  const ended = !isActive(m);
  const who = mandateName(m);
  // The registry's name and digits under who — for a payee named by the customer; otherwise the digits alone.
  const digits = m.to.kind === "payee" ? proxyTail(m.to.masked) : lastDigits(m.to.masked);
  const sub = m.to.kind === "payee" && m.to.nickname !== undefined ? `${m.to.display_name} ${digits}` : digits;
  const next = m.next_due_on;
  const kv: SchedKV[] = [mandateFrom(m)];
  if (!once) {
    const each = eachTimeRow(m.schedule);
    if (each !== undefined) kv.push(each);
    const ends = endsRow(m.schedule);
    if (ends !== undefined) kv.push(ends);
    const most = atMostRow(m.schedule);
    if (most !== undefined) kv.push({ k: most.k, v: `${most.count} · ${most.total}` });
    kv.push({ k: "So far", v: `${m.payments_made} ${m.kind === "bill_payment" ? "paid" : "sent"} · ${m.payments_skipped} skipped · ${m.payments_left} left` });
  }
  kv.push({ k: "Set up", v: fullWhen(m.created_at), small: "Signed with your passkey" });

  const history: HistRow[] = [];
  if (!ended && next !== undefined) {
    history.push({ ic: "i-clock", tone: "next", title: fullDate(next), st: "Next", rest: ` · at ${m.schedule.due_at_local}`, amount: m.amount.kind === "fixed" ? amount : "", muted: true });
  }
  if (m.ended !== undefined && (m.ended.reason === "cancelled" || m.ended.reason === "superseded")) {
    const cancelled = m.ended.reason === "cancelled";
    history.push({ ic: "i-stop", tone: "", title: fullDate(sgDay(m.ended.at)), st: cancelled ? "Stopped" : "Replaced", rest: ` ${cancelled ? "by you " : ""}at ${clock(m.ended.at)}`, amount: "", muted: true });
  }
  for (const o of [...m.occurrences].sort((a, b) => Number(b.seq) - Number(a.seq))) history.push(occurrenceRow(m, o));

  return {
    hero: { who, sub, amount, max, rule: ruleLine(m.schedule), status: ended ? "Status" : "Next payment", flag: ended ? endedFlag(m) : next === undefined ? "" : fullDate(next) },
    kv,
    history,
    ...(ended ? {} : { actions: once ? { stop: "Stop payment", change: "Change amount or date" } : { stop: "Stop payments", change: "Change amount or day" } }),
  };
}

export interface StopWords {
  readonly title: string;
  /** "Up to " before a cap; none for a set amount. */
  readonly pre?: string;
  readonly amount: string;
  readonly lead: string;
  readonly fine?: string;
  readonly act: string;
  readonly keep: string;
  readonly failed: { readonly lead: string; readonly rest: string; readonly act: string };
}

/** 28j, 28l: the stop sheet — one confirm, no passkey; and its loud line when the stop did not get through. */
export function stopSheetWords(m: MandateView): StopWords {
  const { amount, max } = amountOf(m);
  const who = mandateName(m);
  const once = m.schedule.cadence === "once";
  const from = m.from.display_name;
  return {
    title: once ? `Stop the payment to ${who}?` : `Stop the payments to ${who}?`,
    ...(max ? { pre: "Up to " } : {}),
    amount,
    lead: once ? ` on ${shortDate(m.schedule.first_on)}, from ${from}. It won't go out.` : ` ${ruleShort(m.schedule)}, from ${from}. Nothing more will go out from it.`,
    ...(!once && Number(m.payments_made) > 0 ? { fine: "Payments already sent stay sent." } : {}),
    act: once ? "Stop payment" : "Stop payments",
    keep: once ? "Keep it" : "Keep them",
    failed: { lead: "Couldn't stop it.", rest: "It's still on. Try again.", act: "Try again" },
  };
}

/** 28k: the Stopped line at the top of the schedule (route 3 answered, `changed` true or false). */
export function stoppedWords(m: MandateView): { readonly title: string; readonly rest: string } {
  const who = mandateName(m);
  if (m.schedule.cadence !== "once") return { title: "Stopped", rest: `Nothing more will go to ${who} from it.` };
  const { amount, max } = amountOf(m);
  return { title: "Stopped", rest: max ? `The payment to ${who} won't go out.` : `The ${amount} to ${who} won't go out.` };
}
